import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Header from '../../components/Header';
import { api } from '../../api/client';
import { fmtCurrency, fmtPayType } from '../../lib/display-labels';
import {
  ArrowLeft,
  DollarSign,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Calendar,
  Clock,
  Lock
} from 'lucide-react';
import { formatCalendarDate, parseCalendarDate } from '../../lib/calendar-date';
import { payEntryHoursText } from '../../lib/pay-entry-hours';
import { useBack } from '../../hooks/useBack';

// entry_date and period_start/period_end are calendar days, not instants.
function formatDate(dateStr: string) {
  return formatCalendarDate(dateStr, { month: 'short', day: 'numeric' }) ?? '';
}

function formatDateRange(start: string, end: string) {
  const s = formatCalendarDate(start, { month: 'short', day: 'numeric' });
  const e = formatCalendarDate(end, { month: 'short', day: 'numeric', year: 'numeric' });
  return s && e ? `${s} - ${e}` : '';
}

interface TicketGroup {
  ticket_number: string;
  entry_date: string;
  customer_name: string;
  items: any[];
}

function groupEntriesByTicket(entries: any[]): TicketGroup[] {
  const grouped = entries.reduce((acc: Record<string, TicketGroup>, entry: any) => {
    const key = entry.ticket_number || `no-ticket-${entry.id}`;
    if (!acc[key]) {
      acc[key] = {
        ticket_number: key,
        entry_date: entry.entry_date,
        customer_name: entry.customer_name,
        items: [],
      };
    }
    acc[key].items.push(entry);
    return acc;
  }, {} as Record<string, TicketGroup>);

  return Object.values(grouped).sort(
    (a, b) => (parseCalendarDate(a.entry_date)?.getTime() ?? 0) - (parseCalendarDate(b.entry_date)?.getTime() ?? 0)
  );
}

export default function FieldPayScreen() {
  const goBack = useBack('/field');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedTickets, setExpandedTickets] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ['field-my-pay'],
    queryFn: () => api.employee.myPay().then(r => r.data.data ?? r.data ?? []),
    retry: false,
  });

  const is403 = (error as any)?.response?.status === 403;
  const periods: any[] = Array.isArray(data) ? data : [];

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">My Pay</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4">
        {isLoading ? (
          <div className="text-center py-12 text-fieldiq-text">Loading pay info...</div>
        ) : is403 ? (
          <div className="text-center py-12">
            <Lock size={40} className="mx-auto mb-3 text-fieldiq-muted" />
            <p className="text-fieldiq-text font-semibold">Pay information is not available</p>
            <p className="text-sm text-fieldiq-muted mt-1">Contact your administrator for access</p>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-fieldiq-text">Unable to load pay information</div>
        ) : periods.length === 0 ? (
          <div className="text-center py-12 text-fieldiq-text">
            <DollarSign size={40} className="mx-auto mb-3 text-fieldiq-muted" />
            <p>No pay periods found</p>
            <p className="text-xs mt-1 text-fieldiq-muted">Pay stubs will appear here once payroll is processed</p>
          </div>
        ) : (
          <div className="space-y-3">
            {periods.map((period: any) => {
              const isExpanded = expandedId === period.id;
              const entries: any[] = Array.isArray(period.entries) ? period.entries : [];
              const totalHours = entries.reduce((s: number, e: any) => s + Number(e.hours_worked || 0), 0);
              const totalEarnings = Number(period.total_earnings || 0);

              return (
                <div key={period.id} className="bg-fieldiq-card rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : period.id)}
                    className="w-full p-4 flex items-center justify-between text-left"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-fieldiq-accent" />
                        <h3 className="font-semibold text-sm">
                          {formatDateRange(period.period_start, period.period_end)}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-fieldiq-muted">
                        {totalHours > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} /> {totalHours.toFixed(1)}h
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          period.period_status === 'approved' ? 'bg-green-500/20 text-green-400' :
                          period.period_status === 'exported' ? 'bg-green-500/20 text-green-400' :
                          period.period_status === 'open' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-fieldiq-dark text-fieldiq-text'
                        }`}>
                          {period.period_status === 'approved' ? 'Approved' :
                           period.period_status === 'exported' ? 'Paid' :
                           period.period_status === 'open' ? 'Pending' :
                           period.period_status || period.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-green-400">
                        {fmtCurrency(totalEarnings)}
                      </span>
                      {isExpanded ? <ChevronUp size={18} className="text-fieldiq-muted" /> : <ChevronDown size={18} className="text-fieldiq-muted" />}
                    </div>
                  </button>

                  {isExpanded && entries.length > 0 && (
                    <div className="border-t border-fieldiq-border px-4 py-3 space-y-2">
                      {groupEntriesByTicket(entries).map((group) => {
                        const isTicketExpanded = expandedTickets.has(group.ticket_number);
                        const groupEarnings = group.items.reduce((s: number, e: any) => s + Number(e.earnings || 0), 0);

                        const toggleTicket = () => {
                          setExpandedTickets(prev => {
                            const next = new Set(prev);
                            if (next.has(group.ticket_number)) {
                              next.delete(group.ticket_number);
                            } else {
                              next.add(group.ticket_number);
                            }
                            return next;
                          });
                        };

                        return (
                          <div key={group.ticket_number}>
                            <div
                              onClick={toggleTicket}
                              className="flex items-center justify-between text-sm bg-fieldiq-dark rounded-lg px-3 py-2 cursor-pointer"
                            >
                              <div className="flex items-center gap-1">
                                {isTicketExpanded
                                  ? <ChevronDown size={14} className="text-fieldiq-muted shrink-0" />
                                  : <ChevronRight size={14} className="text-fieldiq-muted shrink-0" />}
                                <div>
                                  <span className="text-fieldiq-muted">{formatDate(group.entry_date)}</span>
                                  {group.ticket_number && !group.ticket_number.startsWith('no-ticket-') && (
                                    <>
                                      <span className="mx-2">·</span>
                                      <span>{group.ticket_number}</span>
                                    </>
                                  )}
                                  {group.customer_name && (
                                    <span className="text-fieldiq-muted ml-1 text-xs">({group.customer_name})</span>
                                  )}
                                </div>
                              </div>
                              <span className="text-green-400">{fmtCurrency(groupEarnings)}</span>
                            </div>

                            {isTicketExpanded && (
                              <div className="ml-5 mt-1 space-y-1 border-l-2 border-fieldiq-border pl-3">
                                {group.items.map((entry: any, i: number) => (
                                  <div key={i} className="flex items-center justify-between text-xs text-fieldiq-muted bg-fieldiq-dark/50 rounded px-3 py-1.5">
                                    <div>
                                      {/*
                                        The pay type was rendered RAW here — a
                                        hand with no role on the ticket read
                                        "percentage_triple" on his own pay
                                        screen. Same defect #486 corrected on the
                                        office-side stub, one screen over, and
                                        the one the field actually looks at.

                                        `percentage_applied` may not be on this
                                        endpoint's rows (GET /employee/my-pay,
                                        not the timesheet payload). Absent, the
                                        tier renders bare — which is the house
                                        convention, not a degraded case.
                                      */}
                                      <span className="text-fieldiq-text">
                                        {entry.role_on_ticket
                                          || (entry.pay_type ? fmtPayType(entry.pay_type, entry.percentage_applied) : 'Line item')}
                                      </span>
                                      {/*
                                        Hours by the payroll stub's rule
                                        (payEntryHoursText): hourly rows show
                                        the hours they were paid on, ticket-paid
                                        rows the ticket's hours and span, and a
                                        ticket-paid row whose ticket has no
                                        hours reads a dash, not 0.0.
                                      */}
                                      <span className="ml-2">· {payEntryHoursText(entry)}</span>
                                    </div>
                                    <span className="text-green-400">{fmtCurrency(entry.earnings)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {isExpanded && entries.length === 0 && (
                    <div className="border-t border-fieldiq-border px-4 py-3 text-center text-sm text-fieldiq-muted">
                      No entries for this period
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
